/**
 * InfinitSales - Promotional Modal (Post-Onboarding)
 * Shows plan options to users who just completed onboarding without pre-selecting a plan.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Star, Check, Loader2, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import subscriptionService from '../services/global/subscriptionService';
import { flagShowTrainingToast } from '../hooks/useAgentTrainingNotification';

const PromoModal = ({ isOpen, onClose, plans, title, subtitle }) => {
  const { colors } = useTheme();
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const handleDismiss = useCallback(() => {
    flagShowTrainingToast();
    onClose?.();
  }, [onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') handleDismiss();
  }, [handleDismiss]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const result = await subscriptionService.getCheckoutUrl(planId);
      const checkoutUrl = result.data?.checkout_url || result.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      console.error('[PromoModal] Error getting checkout URL:', err);
      setCheckoutLoading(null);
    }
  };

  if (!isOpen || !plans || plans.length === 0) return null;

  const modal = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleDismiss();
      }}
    >
      <div
        style={{
          backgroundColor: colors.bgPrimary,
          borderRadius: '20px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '40px 32px',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            color: colors.textSecondary,
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              backgroundColor: `${colors.accentPrimary}15`,
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <Sparkles size={28} color={colors.accentPrimary} />
          </div>
          <h2
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: colors.textPrimary,
              margin: '0 0 8px',
            }}
          >
            {title || 'Parabens! Sua conta esta pronta!'}
          </h2>
          <p
            style={{
              fontSize: '15px',
              color: colors.textSecondary,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {subtitle || 'Voce esta no plano Free. Para desbloquear todo o potencial do InfinitSales, assine um dos planos abaixo.'}
          </p>
        </div>

        {/* Plans Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(plans.length, 3)}, 1fr)`,
            gap: '20px',
            marginBottom: '24px',
          }}
        >
          {plans.map((plan) => {
            const isPopular = plan.is_popular;
            const priceFormatted = subscriptionService.formatCurrency(plan.price_brl);

            return (
              <div
                key={plan.id}
                style={{
                  backgroundColor: colors.bgPrimary,
                  borderRadius: '16px',
                  border: isPopular
                    ? `2px solid ${colors.accentPrimary}`
                    : `1px solid ${colors.borderColor}`,
                  padding: '28px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: isPopular
                    ? `0 0 0 1px ${colors.accentPrimary}20, 0 8px 32px ${colors.accentPrimary}15`
                    : 'none',
                }}
              >
                {isPopular && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: colors.accentPrimary,
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '4px 16px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Star size={12} />
                    Popular
                  </div>
                )}

                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: colors.textPrimary,
                    marginBottom: '8px',
                    marginTop: isPopular ? '4px' : 0,
                  }}
                >
                  {plan.name}
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '4px',
                    marginBottom: '20px',
                  }}
                >
                  <span style={{ fontSize: '32px', fontWeight: 800, color: colors.textPrimary }}>
                    {priceFormatted}
                  </span>
                  <span style={{ fontSize: '14px', color: colors.textSecondary }}>/mes</span>
                </div>

                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 24px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  {(plan.features || []).slice(0, 4).map((feature, idx) => (
                    <li
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        fontSize: '13px',
                        color: colors.textSecondary,
                        lineHeight: 1.4,
                      }}
                    >
                      <Check size={14} color={colors.accentPrimary} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={checkoutLoading === plan.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    backgroundColor: isPopular ? colors.accentPrimary : `${colors.accentPrimary}15`,
                    color: isPopular ? 'white' : colors.accentPrimary,
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: checkoutLoading === plan.id ? 'not-allowed' : 'pointer',
                    width: '100%',
                    opacity: checkoutLoading === plan.id ? 0.7 : 1,
                  }}
                >
                  {checkoutLoading === plan.id ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Processando...
                    </>
                  ) : (
                    'Assinar'
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Dismiss link */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleDismiss}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.textSecondary,
              fontSize: '14px',
              cursor: 'pointer',
              padding: '8px 16px',
              textDecoration: 'underline',
            }}
          >
            Continuar no plano Free
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default PromoModal;
